import { z } from "zod";
import { cn } from "@/shared/lib/utils";
import { Skeleton } from "@/shared/components";
import { Info, CheckCircle2, AlertTriangle, AlertCircle } from "lucide-react";
import { defineWidget } from "./types";
import type { AgentWidgetProps } from "./types";
import { registerWidget } from "./widget-registry";
import { WIDGET_TYPES } from "@modootoday/core-agent/constants";

/* ────────────────────── Schema ────────────────────── */

const HighlightBoxSchema = z.object({
  variant: z.enum(["info", "success", "warning", "danger"]),
  title: z.string(),
  body: z.string(),
  bullets: z.array(z.string()).optional(),
});

type HighlightBoxData = z.infer<typeof HighlightBoxSchema>;

/* ────────────────────── Variant config ────────────────────── */

const VARIANT_CONFIG = {
  info: {
    bar: "bg-blue-500",
    bg: "bg-blue-50/60 dark:bg-blue-950/20",
    border: "border-blue-200/60 dark:border-blue-800/40",
    icon: <Info className="size-4 text-blue-500" />,
    titleColor: "text-blue-800 dark:text-blue-300",
    bodyColor: "text-blue-700 dark:text-blue-400",
    bulletColor: "text-blue-600 dark:text-blue-400",
    markerColor: "bg-blue-400",
  },
  success: {
    bar: "bg-emerald-500",
    bg: "bg-emerald-50/60 dark:bg-emerald-950/20",
    border: "border-emerald-200/60 dark:border-emerald-800/40",
    icon: <CheckCircle2 className="size-4 text-emerald-600" />,
    titleColor: "text-emerald-800 dark:text-emerald-300",
    bodyColor: "text-emerald-700 dark:text-emerald-400",
    bulletColor: "text-emerald-600 dark:text-emerald-400",
    markerColor: "bg-emerald-400",
  },
  warning: {
    bar: "bg-amber-500",
    bg: "bg-amber-50/60 dark:bg-amber-950/20",
    border: "border-amber-200/60 dark:border-amber-800/40",
    icon: <AlertTriangle className="size-4 text-amber-600" />,
    titleColor: "text-amber-800 dark:text-amber-300",
    bodyColor: "text-amber-700 dark:text-amber-400",
    bulletColor: "text-amber-600 dark:text-amber-400",
    markerColor: "bg-amber-400",
  },
  danger: {
    bar: "bg-red-500",
    bg: "bg-red-50/60 dark:bg-red-950/20",
    border: "border-red-200/60 dark:border-red-800/40",
    icon: <AlertCircle className="size-4 text-red-500" />,
    titleColor: "text-red-800 dark:text-red-300",
    bodyColor: "text-red-700 dark:text-red-400",
    bulletColor: "text-red-600 dark:text-red-400",
    markerColor: "bg-red-400",
  },
} as const;

/* ────────────────────── Component ────────────────────── */

export function HighlightBox({
  widget,
  onDeepLink,
  className,
}: AgentWidgetProps<HighlightBoxData>) {
  const { variant, title, body, bullets } = widget.data;
  const cfg = VARIANT_CONFIG[variant];

  return (
    <div
      className={cn(
        "flex overflow-hidden rounded-lg border",
        cfg.bg,
        cfg.border,
        className,
      )}
    >
      {/* Left color bar */}
      <div className={cn("w-1 shrink-0", cfg.bar)} />

      {/* Content */}
      <div className="min-w-0 flex-1 px-4 py-3.5">
        {/* Header */}
        <div className="flex items-center gap-2">
          {cfg.icon}
          <p className={cn("text-[13px] font-semibold", cfg.titleColor)}>
            {title}
          </p>
        </div>

        {/* Body */}
        <p className={cn("mt-1.5 text-[12px] leading-relaxed", cfg.bodyColor)}>
          {body}
        </p>

        {/* Bullets */}
        {bullets && bullets.length > 0 && (
          <ul className="mt-2 space-y-1">
            {bullets.map((bullet, idx) => (
              <li
                key={idx}
                className={cn(
                  "flex items-start gap-2 text-[12px]",
                  cfg.bulletColor,
                )}
              >
                <span
                  className={cn(
                    "mt-1.5 size-1.5 shrink-0 rounded-full",
                    cfg.markerColor,
                  )}
                />
                {bullet}
              </li>
            ))}
          </ul>
        )}

        {/* Deep link */}
        {widget.deepLink && onDeepLink && (
          <button
            onClick={() => onDeepLink(widget.deepLink!)}
            className={cn(
              "mt-2.5 text-[12px] font-medium underline-offset-2 hover:underline",
              cfg.titleColor,
            )}
          >
            자세히 보기 →
          </button>
        )}
      </div>
    </div>
  );
}

/* ────────────────────── Skeleton ────────────────────── */

export function HighlightBoxSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex overflow-hidden rounded-xl border border-stroke-subtle",
        className,
      )}
    >
      <div className="w-1 shrink-0 bg-surface-sunken" />
      <div className="flex-1 px-4 py-3.5 space-y-2">
        <div className="flex items-center gap-2">
          <Skeleton className="size-4 rounded-full" />
          <Skeleton className="h-3.5 w-40" />
        </div>
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-3/4" />
      </div>
    </div>
  );
}

/* ────────────────────── Widget Definition ────────────────────── */

const highlightBoxWidget = defineWidget<HighlightBoxData>({
  type: WIDGET_TYPES.HIGHLIGHT_BOX,
  label: "AI 인사이트 하이라이트",
  schema: HighlightBoxSchema,
  component: HighlightBox,
  skeleton: HighlightBoxSkeleton,
  defaultLayout: "full",
  maxHeight: 160,
  aiHint:
    "AI 분석 인사이트 강조 박스 (info/success/warning/danger 4가지 타입, 제목+본문+불릿 포인트)",
});

registerWidget(highlightBoxWidget);
